import { beginPortalRoute } from "./telemetry.ts";
/** A small pushState router: tracker-api falls every portal navigation back to index.html. */
import { useEffect, useRef, useSyncExternalStore, type AnchorHTMLAttributes, type MouseEvent, type ReactNode } from "react";

const listeners = new Set<() => void>();
let preloadNavigation: ((to: string) => void) | null = null;
/** Registers best-effort reads that should begin with a user navigation, never at startup. */
export const setNavigationPreloader = (preloader: ((to: string) => void) | null): void => { preloadNavigation = preloader; };
const notify = () => { for (const fn of listeners) fn(); };
const subscribe = (fn: () => void) => {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
};

/**
 * Navigation blocker (AIC-325): a page with unsaved edits (useNavigationBlocker) is asked before the app leaves it.
 * - In-app navigation (Link, navigate) to another path waits for the page's answer: `proceed` goes on, anything else
 *   stays put. A change of query or #hash on the same path is never blocked.
 * - Browser Back and Forward (popstate) to another path: the URL has already changed when the browser says so, so the
 *   router keeps showing the page, puts the URL back and asks. A second Back while it asks is put back the same way.
 * - Positions: each entry carries its place in the tab's history (history.state.ipdIdx), so putting a Back or Forward
 *   back is history.go(the difference), and `proceed` goes the other way, leaving the browser's history as it was.
 *   The router numbers the entries it pushes from the entry it leaves; an entry something else pushed (the skip link's
 *   #main, a native #hash link, a page's own pushState) is numbered when the router next meets it, as the one after the
 *   last position it saw (kept in sessionStorage, so a reload or a new document in the tab carries on from there
 *   rather than starting again at 0).
 * - Keeping the page on screen while the URL is put back lasts until that popstate arrives, or HOLD_MS at most: a
 *   history.go that goes nowhere (an entry out of reach) can't leave the app showing the old page for good. Then, as
 *   when the entry left has no position or the difference is out of range, the fallback below applies.
 * - Fallback: the router pushes the page's URL again, which drops the Forward entries, and `proceed` is a Back from
 *   that copy. That push is made without a user gesture, so Chrome's history manipulation intervention may skip the
 *   copy on a later Back (entries added without user activation are passed over by the Back button), taking the
 *   user one entry further than they expect.
 * - Leaving the document (reload, another site, closing the tab) is the browser's own beforeunload prompt, which the
 *   page registers while it has unsaved edits.
 */
export interface LeaveAttempt {
  /** Where the navigation was going. */
  readonly to: string;
  /** The control that asked for it (a Link), when there was one: where focus goes back to if the user stays. */
  readonly from: HTMLElement | null;
  /** Carries the navigation out. */
  proceed(): void;
}
type Blocker = (attempt: LeaveAttempt) => void;
let blocker: { active: () => boolean; ask: Blocker } | null = null;

const IDX = "ipdIdx";
/** The key an open drawer's (or a lightbox over one's) history entry carries in its state (components/Drawer.tsx). */
export const DRAWER_ENTRY = "ipdDrawer";
/** How many layers (a drawer, a lightbox over it) the current entry stands for: history entries stacked on the page's own. */
const LAYERS = "ipdLayers";
const layersOf = (state: unknown): number => {
  const s = state && typeof state === "object" ? (state as Record<string, unknown>) : null;
  return s && s[DRAWER_ENTRY] !== undefined ? (typeof s[LAYERS] === "number" ? s[LAYERS] : 1) : 0;
};
const idxOf = (state: unknown): number | null => {
  const v = state && typeof state === "object" ? (state as Record<string, unknown>)[IDX] : undefined;
  return typeof v === "number" ? v : null;
};
const withIdx = (state: unknown, idx: number) => ({ ...(state && typeof state === "object" ? state : {}), [IDX]: idx });
const cleanPath = (p: string) => p.replace(/\/+$/, "") || "/";
const liveHref = () => `${window.location.pathname}${window.location.search}`;
const fullHref = () => `${window.location.pathname}${window.location.search}${window.location.hash}`;

/** The last position the app saw, in memory and in sessionStorage (see Positions above). */
const LAST_KEY = "ipd.router.lastIdx";
let lastIdx: number | null = null;
const readLast = (): number | null => {
  if (lastIdx !== null) return lastIdx;
  try {
    const v = window.sessionStorage.getItem(LAST_KEY);
    return v !== null && /^\d+$/.test(v) ? Number(v) : null;
  } catch {
    return null;
  }
};
const noteIdx = (n: number | null) => {
  if (n === null) return;
  lastIdx = n;
  try { window.sessionStorage.setItem(LAST_KEY, String(n)); } catch { /* no storage: memory only */ }
};
/** The current entry's position, numbering it first (the one after the last seen) when it has none. */
function stamp(): number {
  const at = idxOf(window.history.state);
  if (at !== null) { noteIdx(at); return at; }
  const last = readLast();
  const n = last === null ? 0 : last + 1;
  window.history.replaceState(withIdx(window.history.state, n), "", fullHref());
  noteIdx(n);
  return n;
}

/** The location the app last showed: where a blocked Back or Forward is put back to. */
const NOWHERE = { path: "/", full: "/", state: null as unknown };
let shown = NOWHERE;
/**
 * While a blocked Back or Forward is being put back: the href the app keeps showing, the URL it waits for, and the
 * timer that ends the wait regardless (HOLD_MS).
 */
export const HOLD_MS = 1000;
let holding: string | null = null;
let restoring: string | null = null;
let holdTimer: ReturnType<typeof setTimeout> | null = null;
const clearHold = () => {
  holding = null;
  restoring = null;
  if (holdTimer !== null) clearTimeout(holdTimer);
  holdTimer = null;
};
/** The next popstate is a `proceed`: it goes through. */
let bypass = false;
/** How to carry out the blocked Back or Forward now (the latest one put back): what `proceed` does. */
let undo: (() => void) | null = null;

const snapshot = () => {
  if (holding !== null) return holding;
  shown = { path: cleanPath(window.location.pathname), full: fullHref(), state: window.history.state };
  return liveHref();
};

const leaves = (to: string) => cleanPath(new URL(to, window.location.href).pathname) !== cleanPath(window.location.pathname);

function onPopState(e: PopStateEvent): void {
  if (restoring !== null) {
    const back = fullHref() === restoring;
    clearHold();
    if (back) { noteIdx(idxOf(window.history.state)); e.stopImmediatePropagation(); return; }
  }
  if (bypass) { bypass = false; noteIdx(idxOf(window.history.state)); if (cleanPath(window.location.pathname) !== shown.path) preloadNavigation?.(fullHref()); beginPortalRoute(liveHref(), true); notify(); return; }
  const from = shown;
  const ask = blocking();
  if (!ask || cleanPath(window.location.pathname) === from.path) {
    // On the same page, an entry without a position is one just pushed there (a native #hash link): number it now.
    if (cleanPath(window.location.pathname) === from.path) stamp();
    else noteIdx(idxOf(window.history.state));
    if (cleanPath(window.location.pathname) !== from.path) preloadNavigation?.(fullHref());
    beginPortalRoute(liveHref(), true);
    notify();
    return;
  }
  // Leaving the page with unsaved edits: nothing else hears this one. Put the page's URL back, then ask.
  e.stopImmediatePropagation();
  const to = fullHref();
  const fromIdx = idxOf(from.state);
  const toIdx = idxOf(window.history.state);
  const delta = fromIdx !== null && toIdx !== null ? fromIdx - toIdx : 0;
  const pushBack = () => {
    window.history.pushState(from.state, "", from.full);
    undo = () => { bypass = true; window.history.back(); };
  };
  if (delta !== 0 && Math.abs(delta) < window.history.length) {
    clearHold();
    holding = from.full.replace(/#.*$/, "");
    restoring = from.full;
    holdTimer = setTimeout(() => {
      // The browser didn't go back: stop holding, and put the page's URL back the other way.
      clearHold();
      if (fullHref() !== from.full) pushBack();
      notify();
    }, HOLD_MS);
    undo = () => { bypass = true; window.history.go(-delta); };
    window.history.go(delta);
  } else pushBack();
  ask({ to, from: null, proceed: () => { const u = undo; undo = null; u?.(); } });
}
// Registered once, before any page's own popstate listener, so a blocked Back or Forward can stop them hearing it.
if (typeof window !== "undefined") window.addEventListener("popstate", onPopState);

/** Test-only: forgets the router's module state (blocker, holding, positions), so test cases don't depend on each other. */
export function resetRouterForTests(): void {
  blocker = null;
  clearHold();
  bypass = false;
  undo = null;
  lastIdx = null;
  shown = NOWHERE;
  try { window.sessionStorage.removeItem(LAST_KEY); } catch { /* no storage */ }
}

/**
 * While `active`, the app asks `onBlock` before it leaves this page (see LeaveAttempt). One page at a time: the latest
 * mounted blocker is the one asked. `active` is read when a navigation happens, so a page passes what it knows at once
 * (a ref), not state that reaches the router only after the next render: a Back pressed straight after typing is
 * caught too.
 */
export function useNavigationBlocker(active: () => boolean, onBlock: Blocker): void {
  const latest = useRef({ active, onBlock });
  latest.current = { active, onBlock };
  useEffect(() => {
    const mine: Blocker = (a) => latest.current.onBlock(a);
    blocker = { active: () => latest.current.active(), ask: mine };
    return () => { if (blocker?.ask === mine) blocker = null; };
  }, []);
}
/** The blocker to ask, when one is mounted and active now. */
const blocking = (): Blocker | null => (blocker && blocker.active() ? blocker.ask : null);

export interface NavigateOptions {
  replace?: boolean;
  from?: HTMLElement | null;
  /** A change of what the page shows (a selected step), not a new page: the scroll position and focus stay where they are. */
  keepScroll?: boolean;
}
export function navigate(to: string, opts: NavigateOptions = {}): void {
  const ask = blocking();
  if (ask && leaves(to)) {
    ask({ to, from: opts.from ?? null, proceed: () => go(to, opts) });
    return;
  }
  go(to, opts);
}

function go(to: string, opts: NavigateOptions, unwound = false): void {
  // A navigation always shows where it goes: nothing held from a Back being put back.
  clearHold();
  undo = null;
  // Leaving the page from a lightbox over a drawer: two entries are stacked on the page's. Back out of the top ones first, so the
  // one that is left is replaced below and a single Back from the next page is the page (AIC-503).
  const layers = layersOf(window.history.state);
  if (layers > 1 && !unwound && leaves(to)) {
    let done = false;
    const again = () => { if (done) return; done = true; window.removeEventListener("popstate", again); clearTimeout(giveUp); go(to, opts, true); };
    // If the browser doesn't go back, carry on from where it is, as the hold below does (HOLD_MS).
    const giveUp = setTimeout(again, HOLD_MS);
    window.addEventListener("popstate", again);
    window.history.go(-(layers - 1));
    return;
  }
  // This is the committed navigation point: blocker proceed and ordinary links
  // both arrive here. Starting the prefetch earlier would leave reads alive for
  // a navigation the user chose not to take.
  if (leaves(to)) preloadNavigation?.(to);
  beginPortalRoute(to, true, undefined, opts.replace);
  // The entry being left gets a position too (when it has none), so a blocked Back to it can be put back exactly.
  const at = stamp();
  // Leaving the page from inside a drawer (a link in it, a toast's action): the drawer's own history entry is replaced rather
  // than stacked on, so Back from the next page is one step to the page the drawer was on (AIC-503).
  const fromDrawer = (window.history.state as Record<string, unknown> | null)?.[DRAWER_ENTRY] !== undefined && leaves(to);
  if (opts.replace || fromDrawer) {
    window.history.replaceState(withIdx(null, at), "", to);
    noteIdx(at);
  } else {
    window.history.pushState(withIdx(null, at + 1), "", to);
    noteIdx(at + 1);
  }
  notify();
  if (opts.keepScroll) return;
  // A #hash names a panel on the page (Admin → Cycle#imports, Settings#retention): go to it when it is already there;
  // a page that renders it later scrolls there itself (useHashTarget).
  const hash = to.includes("#") ? to.slice(to.indexOf("#") + 1) : "";
  const target = hash ? document.getElementById(hash) : null;
  if (target) goTo(target);
  else window.scrollTo?.(0, 0);
}

/**
 * Rewrites the current URL in place for a redirect the route resolver already applied (a legacy URL that names another
 * page): the same history entry and position, and no navigation of its own, so no prefetch, leave guard or telemetry
 * route is started for the old URL. Listeners re-read the location.
 */
export function rewriteLocation(to: string): void {
  window.history.replaceState(window.history.state, "", to);
  notify();
}

/**
 * Adds a history entry for the page's own URL, carrying `extra` in its state and the next position: an open drawer's (AIC-503,
 * components/Drawer.tsx), so browser Back closes the drawer. The entry is numbered like one the router made, so a Back or
 * Forward away from the page with unsaved edits is still put back exactly. Nothing navigates: no listener is told.
 */
export function pushEntry(extra: Record<string, unknown>): void {
  const at = stamp();
  window.history.pushState({ ...extra, [IDX]: at + 1, [LAYERS]: layersOf(window.history.state) + 1 }, "", fullHref());
  noteIdx(at + 1);
  shown = { ...shown, state: window.history.state };
}

/**
 * Hands a layer's history entry over to the next one in place (a lightbox that opens another where it closed): the same
 * position and depth, a new state, no entry added and none to take away again.
 */
export function replaceEntry(extra: Record<string, unknown>): void {
  const at = stamp();
  window.history.replaceState({ ...extra, [IDX]: at, [LAYERS]: Math.max(layersOf(window.history.state), 1) }, "", fullHref());
  shown = { ...shown, state: window.history.state };
}

/**
 * Brings a #hash panel into view and, when it is a focus target (it has a tabindex, e.g. -1), moves focus there too,
 * so keyboard and screen-reader users land where the page scrolled (AIC-253).
 */
function goTo(target: HTMLElement): void {
  target.scrollIntoView?.({ block: "start" });
  if (target.hasAttribute("tabindex")) target.focus({ preventScroll: true });
}

/** Goes to the panel the URL's #hash names once `ready` (its data has loaded), once per page visit. */
export function useHashTarget(ready: boolean): void {
  const done = useRef(false);
  useEffect(() => {
    if (!ready || done.current) return;
    done.current = true;
    const hash = window.location.hash.slice(1);
    const target = hash ? document.getElementById(decodeURIComponent(hash)) : null;
    if (target) goTo(target);
  }, [ready]);
}

export interface Route {
  path: string;
  query: URLSearchParams;
}

export function useRoute(): Route {
  const href = useSyncExternalStore(subscribe, snapshot, snapshot);
  const [path = "/", search = ""] = href.split("?");
  return { path: path.replace(/\/+$/, "") || "/", query: new URLSearchParams(search) };
}

/** Hash-only navigation must also reveal a hidden tab or panel, including router.navigate's pushState. */
const subscribeHash = (fn: () => void) => {
  const unsubscribe = subscribe(fn);
  window.addEventListener("hashchange", fn);
  return () => { unsubscribe(); window.removeEventListener("hashchange", fn); };
};
const hashSnapshot = () => window.location.hash;
export function useRouteHash(): string {
  return useSyncExternalStore(subscribeHash, hashSnapshot, () => "");
}

/** Matches `/assignments/:id` style patterns; returns the params or null. */
export function match(pattern: string, path: string): Record<string, string> | null {
  const p = pattern.split("/");
  const s = path.split("/");
  if (p.length !== s.length) return null;
  const out: Record<string, string> = {};
  for (let i = 0; i < p.length; i++) {
    const a = p[i]!;
    const b = s[i]!;
    if (a.startsWith(":")) {
      if (!b) return null;
      out[a.slice(1)] = decodeURIComponent(b);
    } else if (a !== b) return null;
  }
  return out;
}

export function Link({ to, children, ...rest }: { to: string; children: ReactNode } & AnchorHTMLAttributes<HTMLAnchorElement>) {
  const onClick = (e: MouseEvent<HTMLAnchorElement>) => {
    rest.onClick?.(e);
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    navigate(to, { from: e.currentTarget });
  };
  return <a {...rest} href={to} onClick={onClick}>{children}</a>;
}
